import React, { useState } from 'react';
import { login } from '../../../services/dedicated_auth';
import Swal from 'sweetalert2';
import { User, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';

const DedicatedLogin: React.FC = () => {
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    setIsLoading(true);
    try {
      const res = await login(username, password);
      if (res && res.success) {
        window.location.href = '/main';
      } else {
        Swal.fire({
          icon: 'error',
          title: 'เข้าสู่ระบบไม่สำเร็จ',
          text: res.message,
          confirmButtonColor: '#4F46E5',
        });
      }
    } catch (error: any) {
      if (error.response && error.response.status === 429) {
        Swal.fire('ระงับชั่วคราว', 'คุณกดล็อกอินบ่อยเกินไป กรุณารอสักครู่', 'warning');
      } else {
        Swal.fire('Error', 'เกิดข้อผิดพลาดในการเชื่อมต่อ', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{`
        body, html, #root {
          margin: 0;
          padding: 0;
          height: 100%;
          font-family: var(--sook-font);
          background-color: var(--sook-surface);
        }

        .sook-login-wrapper { min-height: 100vh; }

        /* ─── LEFT PANEL ─────────────────────────────── */
        .sook-panel-brand {
          position: relative;
          overflow: hidden;
          padding: 3rem;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }

        .sook-panel-brand::before {
          content: "";
          position: absolute;
          inset: 0;
          background: linear-gradient(
            160deg,
            rgba(79,70,229,0.30) 0%,
            rgba(79,70,229,0.82) 28%,
            rgba(55,48,163,0.78) 55%,
            rgba(30,27,75,0.75) 100%
          );
          z-index: 1;
        }

        .sook-panel-brand::after {
          content: "";
          position: absolute;
          bottom: -80px;
          right: -80px;
          width: 420px;
          height: 420px;
          background: radial-gradient(circle, rgba(245,158,11,0.20) 0%, transparent 70%);
          z-index: 1;
          pointer-events: none;
        }

        .sook-brand-content {
          position: relative;
          z-index: 10;
          color: #ffffff;
          display: flex;
          flex-direction: column;
          height: 100%;
          min-height: calc(100vh - 6rem);
        }

        .sook-brand-logo-row {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .sook-brand-logo-badge {
          background: transparent;
          padding: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .sook-brand-logo-badge img {
          height: 72px;
          width: auto;
          object-fit: contain;
          mix-blend-mode: multiply;
        }



        .sook-brand-headline {
          margin-top: auto;
          padding-bottom: var(--sook-space-6);
        }

        .sook-brand-headline h2 {
          font-family: var(--sook-font);
          font-size: 2.25rem;
          font-weight: 700;
          color: #ffffff;
          line-height: 1.35;
          letter-spacing: -0.5px;
          margin-bottom: var(--sook-space-3);
        }

        .sook-brand-headline p {
          font-size: var(--sook-fs-body-lg);
          color: rgba(255,255,255,0.70);
          line-height: 1.65;
          margin: 0 0 var(--sook-space-4) 0;
        }

        .sook-feature-pills {
          display: flex;
          flex-wrap: wrap;
          gap: var(--sook-space-2);
        }

        .sook-feature-pill {
          display: inline-flex;
          align-items: center;
          padding: 5px 12px;
          background: rgba(255,255,255,0.12);
          border: 1px solid rgba(255,255,255,0.22);
          border-radius: var(--sook-radius-pill);
          font-family: var(--sook-font);
          font-size: var(--sook-fs-label-md);
          font-weight: 500;
          color: rgba(255,255,255,0.90);
          backdrop-filter: blur(2px);
        }

        .sook-brand-footer {
          position: relative;
          z-index: 10;
          font-size: var(--sook-fs-label-sm);
          color: rgba(255,255,255,0.38);
          margin-top: var(--sook-space-6);
        }

        /* ─── RIGHT PANEL ────────────────────────────── */
        .sook-panel-form {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 3rem 2rem;
          background: var(--sook-surface-container-lowest);
        }

        .sook-login-box {
          width: 100%;
          max-width: 420px;
        }

        .sook-form-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: var(--sook-space-2);
          padding: 4px 12px;
          background: var(--sook-primary-bg);
          border-radius: var(--sook-radius-pill);
          font-size: var(--sook-fs-label-md);
          font-weight: 600;
          color: var(--sook-primary-fg);
          margin-bottom: var(--sook-space-4);
          font-family: var(--sook-font);
        }

        .sook-form-title {
          font-family: var(--sook-font);
          font-size: 2rem;
          font-weight: 700;
          color: var(--sook-on-surface);
          line-height: 1.2;
          letter-spacing: -0.5px;
          margin: 0 0 var(--sook-space-2) 0;
        }

        .sook-form-subtitle {
          font-size: var(--sook-fs-body-md);
          color: var(--sook-on-surface-variant);
          margin: 0 0 var(--sook-space-6) 0;
          line-height: 1.5;
          font-family: var(--sook-font);
        }

        .sook-field-label {
          display: block;
          font-family: var(--sook-font);
          font-size: var(--sook-fs-label-md);
          font-weight: 600;
          color: var(--sook-on-surface);
          margin-bottom: var(--sook-space-2);
          letter-spacing: 0.04em;
          text-transform: uppercase;
        }

        .sook-input-wrap { position: relative; }

        .sook-input-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--sook-on-surface-variant);
          pointer-events: none;
        }

        .sook-input {
          width: 100%;
          padding: 0.875rem 1rem 0.875rem 2.75rem;
          border-radius: var(--sook-radius-xl);
          border: 1.5px solid var(--sook-outline-variant);
          background: var(--sook-surface-container-lowest);
          font-family: var(--sook-font);
          font-size: var(--sook-fs-body-md);
          color: var(--sook-on-surface);
          outline: none;
          transition: border-color 150ms ease, box-shadow 150ms ease;
          box-sizing: border-box;
        }

        .sook-input::placeholder {
          color: var(--sook-on-surface-variant);
          opacity: 0.7;
        }

        .sook-input:focus {
          border-color: var(--sook-primary);
          box-shadow: 0 0 0 4px rgba(79,70,229,0.12);
        }

        .sook-input--password { padding-right: 3rem; }

        .sook-eye-btn {
          all: unset;
          cursor: pointer;
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--sook-on-surface-variant);
          display: flex;
          align-items: center;
          padding: 4px;
          border-radius: var(--sook-radius);
          transition: color 120ms ease;
        }

        .sook-eye-btn:hover { color: var(--sook-on-surface); }

        .sook-remember-row {
          display: flex;
          align-items: center;
          gap: var(--sook-space-2);
          margin-bottom: var(--sook-space-6);
        }

        .sook-remember-row input[type="checkbox"] {
          width: 16px;
          height: 16px;
          accent-color: var(--sook-primary);
          cursor: pointer;
          flex-shrink: 0;
        }

        .sook-remember-label {
          font-size: var(--sook-fs-body-sm);
          color: var(--sook-on-surface-variant);
          font-family: var(--sook-font);
          cursor: pointer;
        }

        .sook-btn-primary {
          all: unset;
          box-sizing: border-box;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--sook-space-2);
          padding: 1rem;
          background: var(--sook-primary);
          color: var(--sook-on-primary);
          border-radius: var(--sook-radius-xl);
          font-family: var(--sook-font);
          font-size: var(--sook-fs-body-lg);
          font-weight: 700;
          cursor: pointer;
          transition: background 150ms ease, box-shadow 150ms ease, transform 100ms ease;
          box-shadow: 0 4px 14px rgba(79,70,229,0.30);
        }

        .sook-btn-primary:hover:not(:disabled) {
          background: var(--sook-primary-strong);
          box-shadow: 0 6px 20px rgba(79,70,229,0.35);
          transform: translateY(-1px);
        }

        .sook-btn-primary:active:not(:disabled) { transform: translateY(0); }

        .sook-btn-primary:disabled {
          opacity: 0.65;
          cursor: not-allowed;
          box-shadow: none;
        }


        @media (max-width: 991.98px) {
          .sook-panel-brand { display: none !important; }
          .sook-panel-form  { padding: 2rem 1rem; min-height: 100vh; }
        }
      `}</style>

      <div className="container-fluid p-0">
        <div className="row g-0 sook-login-wrapper">

          {/* ─── LEFT PANEL ──────────────────────────── */}
          <div className="col-lg-6 sook-panel-brand">

            {/* Background food photo */}
            <img
              src="/media/login/1225802_0-600x745-2.webp"
              alt=""
              aria-hidden="true"
              style={{
                position: 'absolute', inset: 0,
                width: '100%', height: '100%',
                objectFit: 'cover', objectPosition: 'center',
                zIndex: 0,
              }}
            />

            <div className="sook-brand-content">

              {/* Brand logo */}
              <div className="sook-brand-logo-row">
                <div className="sook-brand-logo-badge">
                  <img src="/media/login/%E0%B9%82%E0%B8%A5%E0%B9%82%E0%B8%81%E0%B9%89%E0%B8%AA%E0%B8%B8%E0%B8%82%E0%B8%97%E0%B8%B8%E0%B8%81%E0%B8%84%E0%B8%B3-1.png" alt="สุขทุกคำ" />
                </div>
              </div>

              {/* Headline + tagline + pills */}
              <div className="sook-brand-headline">
                <h2>ครัวที่มีระเบียบ<br />คือครัวที่มีกำไร</h2>
                <p>จัดการออเดอร์ ครัว สต็อก และต้นทุน<br />ได้ในที่เดียว ง่าย ชัด แม่นยำ</p>
                <div className="sook-feature-pills">
                  <span className="sook-feature-pill">จัดการออเดอร์</span>
                  <span className="sook-feature-pill">ควบคุมการผลิต</span>
                  <span className="sook-feature-pill">ติดตามสต็อก</span>
                  <span className="sook-feature-pill">วิเคราะห์ต้นทุน</span>
                </div>
              </div>

            </div>
          </div>

          {/* ─── RIGHT PANEL ─────────────────────────── */}
          <div className="col-lg-6 col-12 sook-panel-form">
            <div className="sook-login-box">

              <h1 className="sook-form-title">ยินดีต้อนรับ</h1>
              <p className="sook-form-subtitle">
                กรุณาเข้าสู่ระบบเพื่อเริ่มจัดการร้านของคุณ
              </p>

              <form onSubmit={handleSubmit}>

                {/* Username */}
                <div style={{ marginBottom: '20px' }}>
                  <label className="sook-field-label" htmlFor="sook-username">
                    ชื่อผู้ใช้
                  </label>
                  <div className="sook-input-wrap">
                    <User className="sook-input-icon" size={18} />
                    <input
                      id="sook-username"
                      type="text"
                      className="sook-input"
                      placeholder="กรอกชื่อผู้ใช้"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      autoComplete="username"
                    />
                  </div>
                </div>

                {/* Password */}
                <div style={{ marginBottom: '16px' }}>
                  <label className="sook-field-label" htmlFor="sook-password">
                    รหัสผ่าน
                  </label>
                  <div className="sook-input-wrap">
                    <Lock className="sook-input-icon" size={18} />
                    <input
                      id="sook-password"
                      type={showPassword ? 'text' : 'password'}
                      className="sook-input sook-input--password"
                      placeholder="กรอกรหัสผ่าน"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      className="sook-eye-btn"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                {/* Remember me */}
                <div className="sook-remember-row">
                  <input
                    type="checkbox"
                    id="sook-remember"
                  />
                  <label className="sook-remember-label" htmlFor="sook-remember">
                    จดจำฉันไว้
                  </label>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  className="sook-btn-primary"
                  disabled={isLoading}
                >
                  {isLoading
                    ? <span>กำลังเข้าสู่ระบบ...</span>
                    : (
                      <>
                        <span>เข้าสู่ระบบ</span>
                        <ArrowRight size={18} />
                      </>
                    )
                  }
                </button>

              </form>


            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default DedicatedLogin;
